import {useMemo} from 'react';
import {
  GridActionsCell,
  GridActionsCellItem,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import {assert} from 'assert-ts';
import type {LocalizationContextType} from 'localization/context/types';
import type {Schema} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {Icon} from 'components/icons';
import {FlexBox} from 'components/layout';
import {getFieldRootKeys, getLabel} from 'schema/form/functions';
import {useSchemaCodelists} from 'schema/hooks';
import {
  type BooleanPartOption,
  type ColumnConfig,
  IndicateRowIdFieldName,
  type PartValueDelimiter,
  type PartValueGetters,
  type PartValueRenderers,
  type TableAction,
  type TablePart,
} from '../types';
import {useSchemaThesauruses} from '../../hooks/useSchemaThesauruses';
import {
  mapRenderCell,
  mapType,
  mapValueGetter,
  mapValueOptions,
} from '../mapper';
import {mapFilterOperators} from '../mapper/mapFilterOperators';

export const useDataTableColumns = (
  schema: Schema,
  columnConfig: ColumnConfig[],
  partValueRenderers: PartValueRenderers | undefined,
  partValueGetters: PartValueGetters | undefined,
  partValueDelimiter: PartValueDelimiter | undefined,
  disableFiltering?: boolean,
  disableFilteringByName?: BooleanPartOption,
  disableSorting?: boolean,
  highlights?: string[],
  indicateRowId?: string,
  actions?: TableAction[],
): GridColDef[] => {
  const l = useLocalization();
  const codelistMap = useSchemaCodelists(schema);
  const thesaurusesMap = useSchemaThesauruses(schema);
  const schemaKey = schema.key;

  return useMemo(() => {
    let match = undefined;
    if (highlights && highlights.length > 0) {
      try {
        match = new RegExp(highlights.join('|'), 'gi');
      } catch {
        // Ignore invalid regex. Probably caused by special characters.
      }
    }

    const dataColumns = columnConfig
      // Filter columnConfigs not in schema.
      .filter(a => schema.parts.find(b => (b as TablePart).name === a.name))
      .map<GridColDef>(c => {
        const part = assert(
          schema.parts.find(p => (p as TablePart).name === c.name) as TablePart,
          `useDataTableColumns: Missing ${c.name}`,
          schema.parts,
        );

        const RenderValue = partValueRenderers?.[part.name];
        const customValueGetter = partValueGetters?.[part.name];
        const delimiter = partValueDelimiter?.[part.name];

        return {
          field: part.name as string,
          display: 'flex',
          headerName: getHeaderName(
            l,
            schemaKey,
            part.name as string,
            part.key,
          ),
          width: c.width || 100,
          type: mapType(part.type, part.cardinality),
          valueOptions: mapValueOptions(part, codelistMap),
          filterOperators: mapFilterOperators(part),
          filterable: disableFiltering
            ? false
            : !(disableFilteringByName && disableFilteringByName[part.name]),
          sortable: !disableSorting,
          valueGetter: (value, row) => {
            if (customValueGetter) {
              return customValueGetter(part, value, row);
            } else {
              return mapValueGetter(part, value);
            }
          },
          renderCell: params => {
            return RenderValue ? (
              <RenderValue
                part={part}
                value={params.value}
                match={match}
                delimiter={delimiter}
              />
            ) : (
              mapRenderCell(
                part,
                params,
                l,
                codelistMap,
                thesaurusesMap,
                match,
                delimiter,
              )
            );
          },
        };
      });

    const indicateRowIdCol: GridColDef | undefined = indicateRowId
      ? {
          field: IndicateRowIdFieldName,
          headerName: '',
          maxWidth: 24,
          hideable: false,
          sortable: false,
          filterable: false,
          resizable: false,
          disableColumnMenu: true,
          renderCell: params => {
            return params.id === indicateRowId ? (
              <FlexBox center height={'100%'}>
                <Icon icon="ArrowRight" fontSize="small" />
              </FlexBox>
            ) : null;
          },
        }
      : undefined;

    const actionColDef: GridColDef | undefined =
      indicateRowId && actions && actions.length > 0
        ? {
            field: 'actions',
            type: 'actions',
            renderCell: params => {
              return params.id !== indicateRowId ? (
                <GridActionsCell {...params}>
                  {actions.map(tableAction => {
                    return (
                      <GridActionsCellItem
                        showInMenu
                        onClick={() =>
                          tableAction.onClick(params.row, indicateRowId)
                        }
                        label={tableAction.label}
                      />
                    );
                  })}
                </GridActionsCell>
              ) : null;
            },
          }
        : undefined;

    if (indicateRowIdCol && actionColDef) {
      return [indicateRowIdCol, ...dataColumns, actionColDef];
    }
    if (actionColDef) {
      return [...dataColumns, actionColDef];
    }
    return indicateRowIdCol ? [indicateRowIdCol, ...dataColumns] : dataColumns;
  }, [
    highlights,
    columnConfig,
    indicateRowId,
    actions,
    schema.parts,
    partValueRenderers,
    partValueGetters,
    partValueDelimiter,
    l,
    schemaKey,
    codelistMap,
    disableFiltering,
    disableFilteringByName,
    disableSorting,
    thesaurusesMap,
  ]);
};

const getHeaderName = (
  localization: LocalizationContextType,
  schemaKey: string | undefined,
  partName: string,
  partLabelKey?: string,
): string => {
  const rootKeys = getFieldRootKeys(schemaKey, false, partLabelKey, partName);
  return (
    getLabel(rootKeys, !!partLabelKey, localization) ?? partLabelKey ?? partName
  );
};
